import Vue from 'vue';
import VueRouter, {
    RouteConfig,
} from 'vue-router';
import { routes } from './routes';
import { accessor } from 'src/store/store';
import { type FeatureFlags, testFeatureFlagRequirements } from '@redviking/argonaut-util/src/feature-flags';
import { Notify } from '@redviking/argonaut-core-ui/src/notifications';
import { getRouteFeatureFlags } from './get-route-feature-flags';

// TODO this class doesn't really do much anymore and can probably be removed
/**
 * A VueRouter that puts its routes config and current route info into a vuex store
 */
export class ArgoRouter extends VueRouter {
    addRoutes (routesToAdd: RouteConfig[]): void {
        // TODO check for redundant routes being added
        for (const routeToAdd of routesToAdd) {
            this.addRoute('main', routeToAdd);
        }
    }
}

Vue.use(ArgoRouter); // Vue automatically protects against installing the same plugin twice

function createRouter () {
    return new ArgoRouter({
        mode: 'history',
        routes,
    });
}

export const router = createRouter();

// when leaving an entity route, clear the pending media
router.afterEach((to, from) => {
    const fromEntityRoute = from.matched.find(r => r.meta?.entityType);
    const toEntityRoute = to.matched.find(r => r.meta?.entityType);
    const leavingAnEntityRoute = fromEntityRoute && !toEntityRoute;
    const leavingEditableMode = from.query?.mode !== 'view' && to.query?.mode === 'view';
    if (leavingAnEntityRoute || leavingEditableMode) {
        accessor.clearPendingMedia();
    }
});

const featureFlagsPromise: Promise<Partial<FeatureFlags>> = fetch('/feature-flags')
    .then(res => res.json())
    .then(featureFlags => {
        accessor.setFeatureFlags(featureFlags);
        return featureFlags;
    })
    .catch(err => {
        Notify.error('Failed to fetch feature flags', err);
        return {};
    });


// ensure the required feature flags are present for a navigated route
router.beforeEach(async (to, from, next) => {
    const terminalRoute = to.matched.slice(-1)[0];

    /**
     * if `null`, no feature flag requirement is unspecified, and the navigation will
     * be rejected. To allow a navigation without requiring a feature flag, set the
     * route's `meta.featureFlags = []`
     */
    const routeFeatureFlagReqs = getRouteFeatureFlags(terminalRoute);

    let rejectNavigation = false;

    if (routeFeatureFlagReqs === null) {
        console.error(`No feature flag requirements specified for any of the matched routes for ${to.fullPath}`, { from });
        rejectNavigation = true;
    } else if (routeFeatureFlagReqs.length > 0) {
        const featureFlags = await featureFlagsPromise;
        if (!testFeatureFlagRequirements(routeFeatureFlagReqs, featureFlags)) {
            console.error(`All feature flags are required for ${to.fullPath}`, { routeFeatureFlagReqs });
            rejectNavigation = true;
        }
    }

    if (rejectNavigation) {
        Notify.error('Navigation to this page is not allowed');
        if (from.name === null) {
            // this happens when there is no previous page, so we can't just deny the navigation, we have to redirect.
            next({ name: 'home' });
        } else {
            // deny the navigation
            next(false);
        }
    } else {
        // allow the navigation
        next();
    }
});


// TODO EntityDetail routes should check the meta.canEdit in route configs in the global `beforeResolve` hook, which happens after the `beforeEach` hooks load entity data. Use this to prevent edits on staged configs, for example (which can't be checked until loaded)

if (import.meta.webpackHot) {
    import.meta.webpackHot.accept('src/i18n/i18n'); // no action should be needed
    import.meta.webpackHot.accept('./routes', () => {
        const currentRouteName = router.currentRoute.name;
        // we can't do HMR unless we're on a named route
        if (!currentRouteName) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            import.meta.webpackHot!.decline(); // full reload
            return;
        }

        // we want to replace the routes, but that's only supported in vue-router@^4, which requires vue@^3 (https://github.com/vuejs/vue-router/issues/1234#issuecomment-733841731)
        // luckily, we can use a workaround and simply replace the route matcher, then reset the current route.
        // create a new router, but we'll only use the `matcher`.
        // lovingly ripped from https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
        // @ts-expect-error matcher is not officially exposed
        router.matcher = createRouter().matcher;

        // reset the current route
        router.replace({
            name: currentRouteName,
            params: router.currentRoute.params,
            query: router.currentRoute.query,
            hash: router.currentRoute.hash,
        });
    });
}
